import { lazy, Suspense } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { HomeLandingPage } from "@/pages/HomeLandingPage";
import {
  AGING_WELL_BLOG_PATH,
  MENOPAUSE_BLOG_PATH,
  SLEEP_WELL_BLOG_PATH,
  TIME_FOR_FAMILY_BLOG_PATH,
  BLOG_ROUTES,
  EXPANDED_BLOG_ROUTES,
} from "@/pages/blogRoute";
import { CAMPAIGN_LANDING_ROUTES } from "@/pages/campaigns/campaignLanding";

const ProductPreviewPage = lazy(() => import("@/pages/ProductPreviewPage").then((module) => ({ default: module.ProductPreviewPage })));
const PaymentReturnPage = lazy(() => import("@/pages/PaymentReturnPage").then((module) => ({ default: module.PaymentReturnPage })));
const MenopauseBlogPage = lazy(() => import("@/pages/MenopauseBlogPage").then((module) => ({ default: module.MenopauseBlogPage })));
const AdditionalBlogPage = lazy(() => import("@/pages/AdditionalBlogPage").then((module) => ({ default: module.AdditionalBlogPage })));
const ExpandedBlogPage = lazy(() => import("@/pages/blogs/ExpandedBlogPage").then((module) => ({ default: module.ExpandedBlogPage })));
const CampaignLandingPage = lazy(() => import("@/pages/campaigns/CampaignLandingPage").then((module) => ({ default: module.CampaignLandingPage })));
const CommerceRouteBoundary = lazy(() => import("./CommerceRouteBoundary").then((module) => ({ default: module.CommerceRouteBoundary })));
const AdminRouteBoundary = lazy(() => import("./AdminRouteBoundary").then((module) => ({ default: module.AdminRouteBoundary })));
const ProtectedAdminRoute = lazy(() => import("@/features/admin/ProtectedAdminRoute").then((module) => ({ default: module.ProtectedAdminRoute })));
const loadAdminPages = () => import("@/features/admin/AdminPages");
const AdminLayout = lazy(() => loadAdminPages().then((module) => ({ default: module.AdminLayout })));
const AdminLoginPage = lazy(() => loadAdminPages().then((module) => ({ default: module.AdminLoginPage })));
const AdminProductEditorPage = lazy(() => loadAdminPages().then((module) => ({ default: module.AdminProductEditorPage })));
const AdminProductsPage = lazy(() => loadAdminPages().then((module) => ({ default: module.AdminProductsPage })));

function RouteLoading() {
  return <main className="app-loading" aria-label="Cargando página" role="status">Cargando página…</main>;
}

function deferred(element) {
  return <Suspense fallback={<RouteLoading />}>{element}</Suspense>;
}

function LegacyBlogRedirect({ path }) {
  const { search, hash } = useLocation();
  return <Navigate to={{ pathname: path, search, hash }} replace />;
}

export function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<HomeLandingPage />} />
      <Route path={MENOPAUSE_BLOG_PATH} element={deferred(<MenopauseBlogPage />)} />
      {[TIME_FOR_FAMILY_BLOG_PATH, AGING_WELL_BLOG_PATH, SLEEP_WELL_BLOG_PATH].map((blogPath) => (
        <Route
          path={blogPath}
          key={blogPath}
          element={deferred(<AdditionalBlogPage blogPath={blogPath} />)}
        />
      ))}
      {EXPANDED_BLOG_ROUTES.map(({ path: blogPath }) => (
        <Route
          path={blogPath}
          key={blogPath}
          element={deferred(<ExpandedBlogPage blogPath={blogPath} />)}
        />
      ))}
      {BLOG_ROUTES.filter(({ path, slug }) => path !== `/${slug}`).map(({ path, slug }) => (
        <Route path={`/${slug}`} key={slug} element={<LegacyBlogRedirect path={path} />} />
      ))}
      {CAMPAIGN_LANDING_ROUTES.map(({ id, path }) => (
        <Route
          path={path}
          key={id}
          element={deferred(<CampaignLandingPage campaignId={id} />)}
        />
      ))}
      <Route element={deferred(<CommerceRouteBoundary />)}>
        <Route path="/preview/:id" element={deferred(<ProductPreviewPage />)} />
      </Route>
      <Route path="/payment/paypal/return" element={deferred(<PaymentReturnPage provider="paypal" />)} />
      <Route path="/payment/wompi/return" element={deferred(<PaymentReturnPage provider="wompi" />)} />
      <Route path="/payment/stripe/return" element={deferred(<PaymentReturnPage provider="stripe" />)} />
      <Route element={deferred(<AdminRouteBoundary />)}>
        <Route path="/admin/login" element={deferred(<AdminLoginPage />)} />
        <Route element={deferred(<ProtectedAdminRoute />)}>
          <Route path="/admin" element={<Navigate to="/admin/products" replace />} />
          <Route path="/admin" element={deferred(<AdminLayout />)}>
            <Route path="products" element={deferred(<AdminProductsPage />)} />
            <Route path="products/:id" element={deferred(<AdminProductEditorPage />)} />
          </Route>
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
